"use client";

import React from "react";
import { useTranslations } from "next-intl";
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import NumericText from "@/components/NumericText";
import { getRiskLevelFromScore, getRiskLevelColor } from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/constants/riskConfig";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { cn } from "@/src/lib/utils";
import { SparkleIcon } from "@/components/icons/generated/SparkleIcon";
import { isManualReportGeneration } from "@/lib/branding";

interface ScoreCellProps {
  points: number;
  credibility: number;
  hasTrustScore?: boolean;
  compact?: boolean;
  hidePoints?: boolean;
}

export const ScoreCell: React.FC<ScoreCellProps> = ({
  points,
  credibility,
  hasTrustScore = false,
  compact = false,
  hidePoints = false,
}) => {
  const t = useTranslations();
  const displayValue = credibility < 0 ? 0 : credibility;
  const isZeroCredibility = credibility === 0;
  const rgb = getRiskLevelColor(getRiskLevelFromScore(displayValue));

  const ringFill = hasTrustScore ? rgb : "#e5e7eb";

  const chartData = [
    {
      name: "credibility",
      value: hasTrustScore ? (isZeroCredibility ? 100 : displayValue) : 100,
      fill: ringFill,
    },
  ];

  const chartConfig = {
    value: {
      label: "Credibility",
    },
  } satisfies ChartConfig;

  const ringButton = (
    <Button
      variant="outline"
      size="icon"
      className={cn(
        "pointer-events-none select-none border-0 p-0 shadow-none flex items-center justify-center relative",
        compact ? "h-9 w-9 rounded-none" : "h-9 w-9 rounded-lg"
      )}
    >
      {hasTrustScore && isManualReportGeneration() && (
        <span className="absolute -top-1.5 -right-1.5 z-10 pointer-events-none text-[#B20BFF]">
          <SparkleIcon className="!size-3" />
        </span>
      )}
      <ChartContainer
        config={chartConfig}
        className="aspect-square h-full w-full"
      >
        <RadialBarChart
          data={chartData}
          startAngle={90}
          endAngle={
            hasTrustScore
              ? 90 - (isZeroCredibility ? 360 : credibility < 0 ? 0 : (credibility / 100) * 360)
              : 90
          }
          innerRadius={14}
          outerRadius={20}
        >
          <PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="first:fill-muted last:fill-background"
            polarRadius={[16, 12]}
          />
          <RadialBar dataKey="value" background cornerRadius={10} />
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                    >
                      <tspan
                        dy="0.32em"
                        className={`text-[9px] font-bold ${credibility < 0 ? "fill-foreground" : ""}`}
                        style={credibility >= 0 ? { fill: rgb } : undefined}
                      >
                        {credibility < 0 ? "—" : credibility}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </PolarRadiusAxis>
        </RadialBarChart>
      </ChartContainer>
    </Button>
  );

  if (compact) {
    if (hidePoints) {
      return ringButton;
    }

    return (
      <ButtonGroup className="overflow-hidden rounded-sm border border-gray-200 bg-white shadow-sm">
        <Button
          variant="outline"
          size="icon"
          className="pointer-events-none select-none h-9 min-w-[42px] rounded-none border-y-0 border-l-0 px-3 text-base font-medium shadow-none"
        >
          <NumericText>{points}</NumericText>
        </Button>
        {ringButton}
      </ButtonGroup>
    );
  }

  if (hidePoints) {
    return ringButton;
  }

  return (
    <ButtonGroup className="border-1! border-gray-200! rounded-sm!">
      <Button
        variant="outline"
        size="icon"
        className="pointer-events-none select-none h-auto w-auto rounded-sm border-l-0 border-t-0 border-b-0 p-2 shadow-none flex items-center gap-1.5"
      >
        <NumericText>{points}</NumericText>
        <NumericText className="text-gray-500">
          {t("label-points-min")}
        </NumericText>
      </Button>
      {ringButton}
    </ButtonGroup>
  );
};
